"use client"
import styled from "@emotion/styled"
import { Button, Typography } from "@mui/material"
import Image from "next/image"
import { useEffect, useState } from "react"
import {
  extractProperty,
  Link,
  replaceRichTextLinks,
  versionedAsset,
} from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import { DesktopOnly, LargeDesktop, Mobile } from "./common/bit/MediaQueries"
import { sharedColors } from "./theme/colors"

const BusContainer = styled.div<{ white?: boolean; $reverse?: boolean }>`
  display: flex;
  flex-direction: column;
  position: relative;
  background: ${({ white }) =>
    white ? sharedColors.white : sharedColors.brandDark};
  padding-bottom: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-bottom: 0;
    flex-direction: ${({ $reverse }) => ($reverse ? "row-reverse" : "row")};
    border-radius: 12px;
    min-height: 329px;
    box-shadow: 0px 15px 25px 0px rgba(38, 48, 61, 0.1);
  }
  ${({ theme }) => theme.breakpoints.up("xl")} {
    min-height: 375px;
  }
`

const ContentBox = styled.div`
  z-index: 1;
  display: flex;
  padding: 32px 16px 24px 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 58px 10px 70px 40px;
  }
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  flex: 1 0 0;
`

const StyledUnionContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 72px;
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    height: 85px;
  }
`

const BusImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 167px;
  object-fit: cover;
  ${({ theme }) => theme.breakpoints.up("md")} {
    height: 280px;
    width: 515px;
  }
  ${({ theme }) => theme.breakpoints.up("xl")} {
    height: 375px;
    width: 700px;
  }
`

const StyledLogoContainer = styled.div<{ $reverse?: boolean }>`
  position: absolute;
  z-index: 1;
  bottom: 12px;
  ${({ $reverse }) => $reverse && "left: 16px;"}
  ${({ $reverse }) => !$reverse && "right: 16px;"}
  width: 42px;
  height: 42px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    bottom: 15px;
    ${({ $reverse }) => $reverse && "left: 40px;"}
    ${({ $reverse }) => !$reverse && "right: 40px;"}
    width: 56px;
    height: 56px;
  }
`

const StyledButton = styled(Button)`
  width: 300px;
  ${({ theme }) => theme.breakpoints.down("md")} {
    width: 100%;
  }
  :hover {
    background-color: white;
    color: #008761;
    border: 1px solid #008761;
  }
`

const StyledTypography = styled(Typography)`
  p {
    margin: 0;
  }
`

const StyledBottomText = styled(StyledTypography)`
  position: absolute;
  inset-block-end: 8px;
  inset-inline-start: 16px;
  color: ${sharedColors.brandDark};
  font-size: 16px;
  line-height: 1;
  font-weight: 600;
`

type Props = {
  data: any
  locale: string
}

const busImages = {
  green: {
    bus: {
      big: "/images/services/busBigGreen.png",
      medium: "/images/services/busMediumGreen.png",
      small: "/images/services/busSmallGreen.png",
    },
    union: {
      big: "/images/services/unionBigGreen.png",
      medium: "/images/services/unionMediumGreen.png",
      small: "/images/services/unionSmallGreen.png",
    },
  },
  white: {
    bus: {
      big: "/images/services/busBigWhite.png",
      medium: "/images/services/busMediumWhite.png",
      small: "/images/services/busSmallWhite.png",
    },
    union: {
      big: "/images/services/unionBigWhite.png",
      medium: "/images/services/unionMediumWhite.png",
      small: "/images/services/unionSmallWhite.png",
    },
  },
}

type UndStr = string | undefined

const BasicBanner = ({ data, locale }: Props) => {
  const background = extractProperty(data, "background") as boolean
  const title = extractProperty(data, "title") as UndStr
  const subtitle = extractProperty(data, "subtitle") as UndStr
  const desktopImage = extractProperty(data, "desktopImage") as UndStr
  const mobileImage = extractProperty(data, "mobileImage") as UndStr
  const button = extractProperty(data, "button")
  const bottomText = extractProperty(data, "bottomText")
  const buttonText = button?.[0]?.name as UndStr
  const buttonLink = button?.[0]?.url as UndStr
  const images = background ? busImages.white : busImages.green
  const [isMounted, setIsMounted] = useState(false)
  const reverse = locale === "ru" || locale === "en"
  useEffect(() => {
    setIsMounted(true)
  }, [])

  return (
    <BusContainer white={background} $reverse={reverse}>
      <StyledLogoContainer $reverse={reverse}>
        <Image alt="Egged" src={versionedAsset("/icons/logo.svg")} fill />
      </StyledLogoContainer>
      <ContentBox>
        {isMounted && (
          <>
            <StyledTypography
              variant="h2"
              color="white"
              dangerouslySetInnerHTML={{ __html: title || "" }}
            />
            <StyledTypography
              variant="body1"
              color="white"
              dangerouslySetInnerHTML={{
                __html: replaceRichTextLinks(subtitle || "") || "",
              }}
            />
          </>
        )}
        {buttonText && (
          <StyledButton
            variant="outlineGreen"
            href={buttonLink || ""}
            LinkComponent={Link}
          >
            {buttonText}
          </StyledButton>
        )}
      </ContentBox>
      <Mobile>
        <BusImageContainer>
          <Image
            alt={title || "banner image"}
            src={getUmbracoMediaUrl(mobileImage) || ""}
            fill
          />
        </BusImageContainer>
      </Mobile>
      <DesktopOnly>
        <BusImageContainer>
          <Image
            alt={title || "banner image"}
            src={getUmbracoMediaUrl(desktopImage) || ""}
            fill
          />
        </BusImageContainer>
      </DesktopOnly>
      <LargeDesktop>
        <BusImageContainer>
          <Image
            alt={title || "banner image"}
            src={getUmbracoMediaUrl(desktopImage) || ""}
            fill
          />
        </BusImageContainer>
      </LargeDesktop>
      <StyledUnionContainer>
        <Mobile>
          <Image alt="" src={images.union.small} fill />
        </Mobile>
        <DesktopOnly>
          <Image alt="" src={images.union.medium} fill />
        </DesktopOnly>
        <LargeDesktop>
          <Image alt="" src={images.union.big} fill />
        </LargeDesktop>
        <StyledBottomText>{bottomText}</StyledBottomText>
      </StyledUnionContainer>
    </BusContainer>
  )
}

export default BasicBanner
